import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { DataTable, DataTableRowToggleEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { SelectButton } from "primereact/selectbutton";

// Leaflet Imports
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import type { LatLngExpression } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

import callToast, { handleChangeData, validateFormFields } from "../../../../../utils/helper";
import PageHeading from "../../../../../Components/PageHeading";
import Label from "../../../../../Components/Label";

// Fix Leaflet default icon routing issues in React
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

// Helper component to auto-zoom the map to fit both markers
function MapBoundsUpdater({ pickup, drop }: { pickup: any, drop: any }) {
    const map = useMap();
    useEffect(() => {
        if (pickup && drop) {
            const bounds = L.latLngBounds([pickup.lat, pickup.lng], [drop.lat, drop.lng]);
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
        } else if (pickup) {
            map.setView([pickup.lat, pickup.lng], 13);
        } else if (drop) {
            map.setView([drop.lat, drop.lng], 13);
        }
    }, [pickup, drop, map]);
    return null;
}

export interface TripRecord {
    id: number | null;
    driver: { id: number; name: string } | null;
    route_mode: 'predefined' | 'custom';
    predefined_route: any | null;
    pickup_location: string;
    pickup_coords: { lat: number; lng: number } | null;
    drop_location: string;
    drop_coords: { lat: number; lng: number } | null;
    trip_time: Date | null;
    vehicle: { id: number; name: string } | null;
    status: { id: number; name: string } | null;
    description: string;
}

const PickupAndDrop = () => {
    const mainTitle = "Pickup & Drop Schedule";
    const dhakaCenter = { lat: 23.8103, lng: 90.4125 }; // Default fallback

    // Mock Options
    const statuses = [
        { id: 1, name: 'Pending' },
        { id: 2, name: 'In Transit' },
        { id: 3, name: 'Completed' },
        { id: 4, name: 'Cancelled' },
    ];

    const driverOptions = [
        { id: 1, name: 'Md. Rahim Uddin' },
        { id: 2, name: 'Kamrul Hasan' },
        { id: 3, name: 'Ariful Islam' },
        { id: 4, name: 'Tariqul Islam' }
    ];

    const vehicleOptions = [
        { id: 1, name: 'Toyota Hiace (DM-Ga 11-2345)' },
        { id: 2, name: 'Nissan Civilian (DM-Kha 12-9988)' },
        { id: 3, name: 'Toyota Corolla (DM-Ga 22-4455)' },
        { id: 4, name: 'Microbus (DM-Cha 13-7766)' },
    ];

    const predefinedRoutes = [
        {
            id: 1, label: 'Mirpur 10 to Motijheel',
            pickup: 'Mirpur 10, Dhaka', drop: 'Motijheel C/A, Dhaka',
            pickup_coords: { lat: 23.8069, lng: 90.3687 }, drop_coords: { lat: 23.7275, lng: 90.4186 }
        },
        {
            id: 2, label: 'Uttara to Banani',
            pickup: 'Uttara Sector 7, Dhaka', drop: 'Banani, Dhaka',
            pickup_coords: { lat: 23.8728, lng: 90.3989 }, drop_coords: { lat: 23.7940, lng: 90.4043 }
        },
        {
            id: 3, label: 'Dhanmondi to Gulshan',
            pickup: 'Dhanmondi 27, Dhaka', drop: 'Gulshan 1, Dhaka',
            pickup_coords: { lat: 23.7561, lng: 90.3755 }, drop_coords: { lat: 23.7806, lng: 90.4162 }
        }
    ];

    const routeModeOptions = [
        { label: 'Predefined Route', value: 'predefined' },
        { label: 'Custom Map Route', value: 'custom' }
    ];

    const initialState: TripRecord = {
        id: null,
        driver: null,
        route_mode: 'predefined',
        predefined_route: null,
        pickup_location: '',
        pickup_coords: null,
        drop_location: '',
        drop_coords: null,
        trip_time: null,
        vehicle: null,
        status: statuses[0], // Defaults to Pending for new creations
        description: ""
    };

    const toast = useRef<Toast | null>(null);
    const [formData, setFormData] = useState<TripRecord>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [modal, setModal] = useState<boolean>(false);
    const [updatedId, setUpdatedId] = useState<number | null>(null);
    const [expandedRows, setExpandedRows] = useState<any | undefined>(undefined);

    const pickupMarkerRef = useRef<L.Marker>(null);
    const dropMarkerRef = useRef<L.Marker>(null);

    const [tableDataList, setTableDataList] = useState<TripRecord[]>([]);

    useEffect(() => {
        const mockData: TripRecord[] = [
            {
                id: 1,
                driver: driverOptions[0],
                route_mode: 'predefined',
                predefined_route: predefinedRoutes[0],
                pickup_location: predefinedRoutes[0].pickup,
                pickup_coords: predefinedRoutes[0].pickup_coords,
                drop_location: predefinedRoutes[0].drop,
                drop_coords: predefinedRoutes[0].drop_coords,
                trip_time: new Date('2026-07-26T08:00:00'),
                vehicle: vehicleOptions[0],
                status: statuses[1], // In Transit
                description: 'Regular morning office pickup. VIP Guest included.'
            },
            {
                id: 2,
                driver: driverOptions[1],
                route_mode: 'predefined',
                predefined_route: predefinedRoutes[1],
                pickup_location: predefinedRoutes[1].pickup,
                pickup_coords: predefinedRoutes[1].pickup_coords,
                drop_location: predefinedRoutes[1].drop,
                drop_coords: predefinedRoutes[1].drop_coords,
                trip_time: new Date('2026-07-26T14:30:00'),
                vehicle: vehicleOptions[1],
                status: statuses[0], // Pending
                description: 'Afternoon shift staff drop-off.'
            },
            {
                id: 3,
                driver: driverOptions[2],
                route_mode: 'custom',
                predefined_route: null,
                pickup_location: 'Hazrat Shahjalal International Airport',
                pickup_coords: { lat: 23.8433, lng: 90.4049 },
                drop_location: 'Pan Pacific Sonargaon, Dhaka',
                drop_coords: { lat: 23.7503, lng: 90.3934 },
                trip_time: new Date('2026-07-25T21:00:00'),
                vehicle: vehicleOptions[2],
                status: statuses[2], // Completed
                description: 'Airport pickup for foreign delegates.'
            },
            {
                id: 4,
                driver: driverOptions[3],
                route_mode: 'predefined',
                predefined_route: predefinedRoutes[2],
                pickup_location: predefinedRoutes[2].pickup,
                pickup_coords: predefinedRoutes[2].pickup_coords,
                drop_location: predefinedRoutes[2].drop,
                drop_coords: predefinedRoutes[2].drop_coords,
                trip_time: new Date('2026-07-27T10:00:00'),
                vehicle: vehicleOptions[3],
                status: statuses[3], // Cancelled
                description: 'Meeting was rescheduled.'
            },
            {
                id: 5,
                driver: driverOptions[0],
                route_mode: 'custom',
                predefined_route: null,
                pickup_location: 'Bashundhara R/A',
                pickup_coords: { lat: 23.8188, lng: 90.4328 },
                drop_location: 'Badda Link Road',
                drop_coords: { lat: 23.7744, lng: 90.4262 },
                trip_time: new Date('2026-07-28T09:15:00'),
                vehicle: vehicleOptions[0],
                status: statuses[0], // Pending
                description: ''
            }
        ];
        setTableDataList(mockData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Form Submission
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const baseRules = [
            { field: 'driver', label: 'Driver Name' },
            { field: 'trip_time', label: 'Time' },
            { field: 'vehicle', label: 'Vehicle' }
        ];

        if (updatedId) {
            baseRules.push({ field: 'status', label: 'Status' });
        }

        const routeRules = formData.route_mode === 'predefined'
            ? [{ field: 'predefined_route', label: 'Predefined Route' }]
            : [
                { field: 'pickup_location', label: 'Pickup Location Name' },
                { field: 'drop_location', label: 'Drop Location Name' }
            ];

        if (!validateFormFields(formData, [...baseRules, ...routeRules], toast)) return;

        setIsSubmitting(true);

        setTimeout(() => {
            if (updatedId) {
                setTableDataList(tableDataList.map(item => item.id === updatedId ? { ...formData, id: updatedId } : item));
                callToast(toast, true, `Trip updated successfully`);
            } else {
                const newId = tableDataList.length > 0 ? Math.max(...tableDataList.map(t => t.id as number)) + 1 : 1;
                setTableDataList([{ ...formData, id: newId }, ...tableDataList]);
                callToast(toast, true, `Trip scheduled successfully`);
            }

            setFormData(initialState);
            setModal(false);
            setUpdatedId(null);
            setIsSubmitting(false);
        }, 500);
    };

    // Mode switch handler
    const handleRouteModeChange = (mode: string) => {
        if (!mode) return;

        let updated = { ...formData, route_mode: mode as 'predefined' | 'custom' };

        // If switching to custom and no coords exist, provide default draggable markers in Dhaka
        if (mode === 'custom') {
            if (!updated.pickup_coords) updated.pickup_coords = { lat: 23.8103, lng: 90.4125 };
            if (!updated.drop_coords) updated.drop_coords = { lat: 23.7900, lng: 90.4000 };
            updated.predefined_route = null;
        }

        setFormData(updated);
    };

    // Predefined Route Selection handler
    const handlePredefinedRouteSelect = (route: any) => {
        setFormData({
            ...formData,
            predefined_route: route,
            pickup_location: route ? route.pickup : '',
            drop_location: route ? route.drop : '',
            pickup_coords: route ? route.pickup_coords : null,
            drop_coords: route ? route.drop_coords : null,
        });
    };

    // Marker Drag Handlers for Custom Mode
    const pickupDragHandlers = useMemo(() => ({
        dragend() {
            const marker = pickupMarkerRef.current;
            if (marker) {
                const { lat, lng } = marker.getLatLng();
                setFormData(prev => ({ ...prev, pickup_coords: { lat, lng } }));
            }
        },
    }), []);

    const dropDragHandlers = useMemo(() => ({
        dragend() {
            const marker = dropMarkerRef.current;
            if (marker) {
                const { lat, lng } = marker.getLatLng();
                setFormData(prev => ({ ...prev, drop_coords: { lat, lng } }));
            }
        },
    }), []);


    // UI Helpers for Table
    function message(msg: string, status: string) {
        return <span className={`custom-badge ${status}`}>{msg}</span>;
    }

    const statusBodyTemplate = (rowData: TripRecord) => {
        const statusId = rowData.status?.id;
        return (
            <span className={'w-full'}>
                {statusId === 1 ? message("Pending", "pending")
                    : statusId === 2 ? message("In Transit", "approved")
                        : statusId === 3 ? message("Completed", "success")
                            : statusId === 4 ? message("Cancelled", "cancelled")
                                : message("Unknown", "neutral")}
            </span>
        );
    };

    const locationBodyTemplate = (rowData: TripRecord) => {
        return (
            <div className="flex flex-column gap-1">
                <span className="font-semibold text-900 text-sm">{rowData.pickup_location}</span>
                <span className="text-500 text-xs flex align-items-center">
                    <i className="pi pi-arrow-down mr-1" style={{ fontSize: '0.6rem' }} /> To
                </span>
                <span className="text-700 text-sm">{rowData.drop_location}</span>
            </div>
        );
    };

    const actionTemplate = (rowData: TripRecord) => {
        return (
            <div className={'flex align-items-center justify-content-end'}>
                <Button
                    icon={'pi pi-pencil'}
                    className={'border-rounded w-2rem h-2rem'}
                    onClick={() => {
                        setModal(true);
                        setUpdatedId(rowData.id);
                        setFormData({ ...rowData });
                    }}
                    outlined
                />
            </div>
        );
    };

    const rowExpansionTemplate = (data: TripRecord) => {
        return (
            <div className="p-4 rounded-md shadow-sm surface-50">
                <h6 className="text-lg font-semibold mb-2 text-gray-700 flex align-items-center gap-2">
                    <i className="pi pi-info-circle text-primary"></i> Trip Notes
                </h6>
                <p className="text-gray-600 text-base m-0">{data.description}</p>
            </div>
        );
    };

    return (
        <div className={"card"}>
            <Toast ref={toast} />
            <PageHeading
                title={mainTitle}
                isCreate={true}
                createFunction={() => {
                    setFormData(initialState);
                    setUpdatedId(null);
                    setModal(true);
                }}
                back={true}
            />

            <DataTable
                value={tableDataList}
                dataKey="id"
                tableStyle={{ minWidth: '60rem' }}
                paginator
                rows={10}
                expandedRows={expandedRows}
                onRowToggle={(e: DataTableRowToggleEvent) => setExpandedRows(e?.data)}
                rowExpansionTemplate={rowExpansionTemplate}
                emptyMessage="No trips scheduled."
            >
                <Column expander={(r) => r.description.length > 0} style={{ width: '3rem' }} />
                <Column field="driver.name" header="Driver" style={{ width: '15%' }} className="font-semibold" />
                <Column header="Route (From/To)" style={{ width: '25%' }} body={locationBodyTemplate} />
                <Column field="trip_time" header="Schedule Time" style={{ width: '15%' }} body={(r) => r.trip_time ? new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(r.trip_time)) : '-'} />
                <Column field="vehicle.name" header="Assigned Vehicle" style={{ width: '20%' }} />
                <Column field="status" header="Status" style={{ width: '10%' }} body={statusBodyTemplate} />
                <Column header={"Action"} bodyStyle={{ textAlign: 'center' }} body={actionTemplate} headerClassName={"flex justify-content-end"} />
            </DataTable>

            {/* CREATE / EDIT DIALOG */}
            <Dialog
                visible={modal}
                style={{ width: "60rem" }}
                headerClassName="text-800 font-semibold text-xl"
                header={updatedId ? `Edit ${mainTitle}` : `New ${mainTitle}`}
                breakpoints={{ "960px": "75vw", "640px": "90vw" }}
                onHide={() => {
                    setUpdatedId(null);
                    setModal(false);
                    setFormData(initialState);
                }}
            >
                <div>
                    <form onSubmit={handleSubmit}>
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <Label label={"Driver Name"} required={!updatedId} />
                                <Dropdown
                                    value={formData.driver}
                                    options={driverOptions}
                                    optionLabel="name"
                                    onChange={(e) => handleChangeData('driver', e.value, formData, setFormData)}
                                    placeholder={"Select a Driver"}
                                    className={`w-full`}
                                    filter
                                />
                            </div>

                            <div className="col-12 md:col-6">
                                <Label label={"Schedule Time"} required={!updatedId} />
                                <Calendar
                                    value={formData.trip_time}
                                    onChange={(e) => handleChangeData('trip_time', e.value, formData, setFormData)}
                                    showTime
                                    hourFormat="12"
                                    placeholder="Select Date and Time"
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {/* ROUTING SECTION */}
                        <div className="grid mt-3">
                            <div className="col-12 border-top-1 surface-border pt-3">
                                <Label label={"Route Selection Mode"} />
                                <SelectButton
                                    value={formData.route_mode}
                                    options={routeModeOptions}
                                    onChange={(e) => handleRouteModeChange(e.value)}
                                    className="mb-3"
                                />

                                {/* Form Inputs based on Mode */}
                                {formData.route_mode === 'predefined' ? (
                                    <div className="grid mb-3">
                                        <div className="col-12">
                                            <Label label={"Select Predefined Route"} required />
                                            <Dropdown
                                                options={predefinedRoutes}
                                                value={formData.predefined_route}
                                                onChange={(e) => handlePredefinedRouteSelect(e.value)}
                                                optionLabel="label"
                                                placeholder="Select a route"
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid mb-3">
                                        <div className="col-12 md:col-6">
                                            <Label label={"Pickup Location Name"} required />
                                            <InputText
                                                value={formData.pickup_location}
                                                onChange={(e) => handleChangeData('pickup_location', e.target.value, formData, setFormData)}
                                                placeholder={"e.g., Dhanmondi 27"}
                                                className="w-full"
                                            />
                                        </div>
                                        <div className="col-12 md:col-6">
                                            <Label label={"Drop Location Name"} required />
                                            <InputText
                                                value={formData.drop_location}
                                                onChange={(e) => handleChangeData('drop_location', e.target.value, formData, setFormData)}
                                                placeholder={"e.g., Gulshan 1"}
                                                className="w-full"
                                            />
                                        </div>
                                        {/* Helper instruction for custom mode */}
                                        <div className="col-12 text-500 text-sm mt-1">
                                            <i className="pi pi-info-circle mr-1"></i>
                                            Drag the markers on the map below to set exact coordinates.
                                        </div>
                                    </div>
                                )}

                                {/* EMBEDDED MAP CONTAINER */}
                                <div className="border-round shadow-1 overflow-hidden" style={{ height: '350px', width: '100%', position: 'relative' }}>
                                    {modal && (
                                        <MapContainer
                                            center={[dhakaCenter.lat, dhakaCenter.lng]}
                                            zoom={12}
                                            style={{ height: '100%', width: '100%', zIndex: 1 }}
                                        >
                                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                                            {/* Pickup Marker */}
                                            {formData.pickup_coords && (
                                                <Marker
                                                    position={[formData.pickup_coords.lat, formData.pickup_coords.lng]}
                                                    draggable={formData.route_mode === 'custom'}
                                                    ref={pickupMarkerRef}
                                                    eventHandlers={pickupDragHandlers}
                                                >
                                                    <Tooltip permanent direction="top" offset={[0, -20]} className="font-bold text-green-600">
                                                        Pickup (Start)
                                                    </Tooltip>
                                                </Marker>
                                            )}

                                            {/* Drop Marker */}
                                            {formData.drop_coords && (
                                                <Marker
                                                    position={[formData.drop_coords.lat, formData.drop_coords.lng]}
                                                    draggable={formData.route_mode === 'custom'}
                                                    ref={dropMarkerRef}
                                                    eventHandlers={dropDragHandlers}
                                                >
                                                    <Tooltip permanent direction="top" offset={[0, -20]} className="font-bold text-red-600">
                                                        Drop-off (End)
                                                    </Tooltip>
                                                </Marker>
                                            )}

                                            {/* Auto Center/Zoom to fit markers */}
                                            <MapBoundsUpdater pickup={formData.pickup_coords} drop={formData.drop_coords} />
                                        </MapContainer>
                                    )}
                                </div>

                            </div>
                        </div>

                        <div className="grid mt-3 border-top-1 surface-border pt-3">
                            <div className="col-12 md:col-6">
                                <Label label={"Assign Vehicle"} required={!updatedId} />
                                <Dropdown
                                    options={vehicleOptions}
                                    optionLabel="name"
                                    placeholder="Select Vehicle"
                                    value={formData.vehicle}
                                    onChange={(e) => handleChangeData('vehicle', e.value, formData, setFormData)}
                                    className="w-full"
                                    filter
                                />
                            </div>

                            {/* Only show Status dropdown when editing an existing trip */}
                            {updatedId && (
                                <div className="col-12 md:col-6">
                                    <Label label={"Status"} required={!updatedId} />
                                    <Dropdown
                                        options={statuses}
                                        optionLabel="name"
                                        placeholder="Select status"
                                        value={formData.status}
                                        onChange={(e) => handleChangeData('status', e.value, formData, setFormData)}
                                        className="w-full"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="grid mt-2">
                            <div className="col-12">
                                <Label label={"Trip Notes / Description"} />
                                <InputTextarea
                                    value={formData.description}
                                    rows={2}
                                    className={`w-full`}
                                    onChange={(e) => handleChangeData('description', e.target.value, formData, setFormData)}
                                    placeholder={"Enter special instructions"}
                                />
                            </div>
                        </div>

                        <div className="flex justify-content-end gap-2 mt-4">
                            <Button type="button" label="Cancel" icon="pi pi-times" severity="secondary" onClick={() => setModal(false)} disabled={isSubmitting} />
                            <Button type="submit" icon="pi pi-check" label={updatedId ? 'Update Trip' : "Schedule Trip"} className="p-button-primary" loading={isSubmitting} />
                        </div>
                    </form>
                </div>
            </Dialog>
        </div>
    );
};

export default PickupAndDrop;